/**
 * The Clients list: one row per client — name and terms, its locations, its
 * point of contact, the next job (or the last), what it owes and whether it
 * pays on time — with search, filters, sorting and the four numbers above it
 * (design/prototype/Clients.dc.html, docs/majik/PROTOTYPE-DECISIONS.md →
 * Clients). Pure — the server gathers the records and calls `listClients`.
 */
import { z } from 'zod'
import type { Contact } from '../contacts/contact.ts'
import { contactName } from '../contacts/contact.ts'
import { type Invoice, isOverdue } from '../invoices/invoice.ts'
import type { Job, WorkType } from '../jobs/job.ts'
import { csvLine } from '../mcp/exports.ts'
import { clockAt, weekdayDay } from '../shared/zoned.ts'
import {
  type BillingItem,
  type BillingMethod,
  billingMethodLabels,
  billingMethods,
  type Client,
  type ClientPerson,
  clientStatus,
  type Location,
  type PaymentTerms,
  paymentTermLabels,
  paymentTerms,
  type ServiceStatus,
} from './client.ts'

export const clientSortKeys = ['name', 'locations', 'contact', 'job', 'owed', 'payment'] as const
export type ClientSortKey = (typeof clientSortKeys)[number]

export const paymentStanding = ['on_time', 'overdue'] as const
export type PaymentStanding = (typeof paymentStanding)[number]
export const locationCounts = ['one', 'several'] as const

export const clientsQuerySchema = z.object({
  q: z.string().trim().max(200).default(''),
  billing: z.array(z.enum(billingMethods)).default([]),
  payment: z.array(z.enum(paymentStanding)).default([]),
  terms: z.array(z.enum(paymentTerms)).default([]),
  locations: z.array(z.enum(locationCounts)).default([]),
  sort: z
    .object({ key: z.enum(clientSortKeys), dir: z.enum(['asc', 'desc']) })
    .nullable()
    .default(null),
  page: z.number().int().min(0).default(0),
  pageSize: z.number().int().min(1).max(100).default(25),
})
export type ClientsQuery = z.infer<typeof clientsQuerySchema>
export type ClientsQueryInput = z.input<typeof clientsQuerySchema>

export interface ClientsContext {
  now: string
  timeZone: string
  overdueAfterDays: number
  locations: readonly Location[]
  people: readonly ClientPerson[]
  contacts: ReadonlyMap<string, Contact>
  invoices: readonly Invoice[]
  /** Every job not deleted and not cancelled — for the next or last visit. */
  jobs: readonly Job[]
  workTypes: ReadonlyMap<string, WorkType>
  billingItems: readonly BillingItem[]
}

export interface ClientRow {
  id: string
  name: string
  status: ServiceStatus
  terms: string
  paymentTerms: PaymentTerms
  billingMethod: BillingMethod
  billingLabel: string
  locationCount: number
  /** The first location's street, or "(all)" when several share it. */
  locationLine: string
  contactName: string
  contactCount: number
  /** 'Thu, Oct 22 · 9:30 AM', or '' when the client has never had a job. */
  jobDay: string
  /** 'Next · Office Cleaning' or 'Last · Office Cleaning'. */
  jobLine: string
  jobAt: string
  owed: number
  unpaidCount: number
  overdueCount: number
  overdueTotal: number
  monthly: number
}

const sum = (values: readonly number[]) => values.reduce((total, v) => total + v, 0)
const owing = (invoice: Invoice) =>
  invoice.status === 'sent' && (invoice.balanceDue === null || invoice.balanceDue > 0)

export function clientRow(client: Client, ctx: ClientsContext): ClientRow {
  const locations = ctx.locations.filter((l) => l.clientId === client.id && !l.deletedAt)
  const live = locations.filter((l) => l.status !== 'inactive')
  const people = ctx.people.filter((p) => p.clientId === client.id)
  const poc =
    people.find((p) => p.role === 'point_of_contact' && p.isPrimary) ??
    people.find((p) => p.role === 'point_of_contact') ??
    people[0]
  const contact = poc ? ctx.contacts.get(poc.contactId) : undefined
  const unpaid = ctx.invoices.filter((i) => i.clientId === client.id && owing(i))
  const overdue = unpaid.filter((i) => isOverdue(i, ctx.now, ctx.overdueAfterDays))
  const ids = new Set(locations.map((l) => l.id))
  const jobs = ctx.jobs
    .filter((j) => ids.has(j.locationId) && j.status !== 'cancelled')
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
  const next = jobs.find((j) => j.scheduledAt >= ctx.now && j.status === 'scheduled')
  const last = [...jobs].reverse().find((j) => j.scheduledAt < ctx.now)
  const shown = next ?? last
  const workType = shown ? (ctx.workTypes.get(shown.workTypeIds[0] ?? '')?.name ?? 'Visit') : ''
  const streets = new Set(live.map((l) => l.addressLine1))
  const first = live[0] ?? locations[0]
  return {
    id: client.id,
    name: client.name,
    status: clientStatus(locations),
    terms: paymentTermLabels[client.paymentTerms],
    paymentTerms: client.paymentTerms,
    billingMethod: client.billingMethod,
    billingLabel: billingMethodLabels[client.billingMethod],
    locationCount: live.length,
    locationLine: first
      ? `${first.addressLine1}${live.length > 1 && streets.size === 1 ? ' (all)' : ''}`
      : 'No locations yet',
    contactName: contact ? contactName(contact) : 'No contact yet',
    contactCount: people.length,
    jobDay: shown
      ? next
        ? `${weekdayDay(shown.scheduledAt, ctx.timeZone)} · ${clockAt(shown.scheduledAt, ctx.timeZone)}`
        : weekdayDay(shown.scheduledAt, ctx.timeZone)
      : '',
    jobLine: shown ? `${next ? 'Next' : 'Last'} · ${workType}` : 'No jobs yet',
    jobAt: shown?.scheduledAt ?? '',
    owed: sum(unpaid.map((i) => i.balanceDue ?? i.quickbooksTotal ?? 0)),
    unpaidCount: unpaid.length,
    overdueCount: overdue.length,
    overdueTotal: sum(overdue.map((i) => i.balanceDue ?? i.quickbooksTotal ?? 0)),
    monthly: sum(
      ctx.billingItems
        .filter((b) => b.clientId === client.id && b.active)
        .map((b) => b.unitPrice * b.quantity),
    ),
  }
}

export interface ClientFacetOption {
  value: string
  label: string
  count: number
}

export interface ClientsList {
  rows: ClientRow[]
  total: number
  base: number
  page: number
  pageCount: number
  stats: {
    clients: number
    locations: number
    owed: number
    unpaid: number
    overdue: number
    overdueClients: number
    monthly: number
  }
  facets: {
    billing: ClientFacetOption[]
    payment: ClientFacetOption[]
    terms: ClientFacetOption[]
    locations: ClientFacetOption[]
  }
}

type Facet = 'billing' | 'payment' | 'terms' | 'locations'
const standing = (row: ClientRow): PaymentStanding => (row.overdueCount > 0 ? 'overdue' : 'on_time')
const count = (row: ClientRow) => (row.locationCount > 1 ? 'several' : 'one')

function passes(row: ClientRow, query: ClientsQuery, haystack: string, skip?: Facet): boolean {
  if (query.q && !haystack.includes(query.q.toLowerCase())) return false
  const any = <T extends string>(key: Facet, values: readonly T[], value: string) =>
    key === skip || values.length === 0 || values.includes(value as T)
  return (
    any('billing', query.billing, row.billingMethod) &&
    any('payment', query.payment, standing(row)) &&
    any('terms', query.terms, row.paymentTerms) &&
    any('locations', query.locations, count(row))
  )
}

const SORT: Record<ClientSortKey, (row: ClientRow) => string | number> = {
  name: (row) => row.name.toLowerCase(),
  locations: (row) => row.locationCount,
  contact: (row) => row.contactName.toLowerCase(),
  job: (row) => row.jobAt,
  owed: (row) => row.owed,
  payment: (row) => row.overdueCount,
}

export function listClients(
  clients: readonly Client[],
  ctx: ClientsContext,
  query: ClientsQuery,
  /** Words each client is found by: its contacts, addresses, emails. */
  words: (client: Client) => string,
): ClientsList {
  const all = clients.map((client) => ({ client, row: clientRow(client, ctx) }))
  const hay = new Map(
    all.map(({ client, row }) => [row.id, `${row.name} ${words(client)}`.toLowerCase()]),
  )
  const pass = (row: ClientRow, skip?: Facet) => passes(row, query, hay.get(row.id) ?? '', skip)
  const rows = all.map(({ row }) => row)
  const sort = query.sort ?? { key: 'name' as const, dir: 'asc' as const }
  const dir = sort.dir === 'asc' ? 1 : -1
  const filtered = rows
    .filter((row) => pass(row))
    .sort((a, b) => {
      const x = SORT[sort.key](a)
      const y = SORT[sort.key](b)
      return (x < y ? -dir : x > y ? dir : 0) || a.name.localeCompare(b.name)
    })
  const pageCount = Math.max(1, Math.ceil(filtered.length / query.pageSize))
  const page = Math.min(query.page, pageCount - 1)
  const facet = (
    key: Facet,
    options: { value: string; label: string }[],
    of: (r: ClientRow) => string,
  ) => {
    const pool = rows.filter((row) => pass(row, key))
    return options
      .filter((o) => rows.some((row) => of(row) === o.value))
      .map((o) => ({ ...o, count: pool.filter((row) => of(row) === o.value).length }))
  }
  return {
    rows: filtered.slice(page * query.pageSize, (page + 1) * query.pageSize),
    total: filtered.length,
    base: rows.length,
    page,
    pageCount,
    stats: {
      clients: rows.length,
      locations: sum(rows.map((r) => r.locationCount)),
      owed: sum(rows.map((r) => r.owed)),
      unpaid: sum(rows.map((r) => r.unpaidCount)),
      overdue: sum(rows.map((r) => r.overdueTotal)),
      overdueClients: rows.filter((r) => r.overdueCount > 0).length,
      monthly: sum(rows.map((r) => r.monthly)),
    },
    facets: {
      billing: facet(
        'billing',
        billingMethods.map((m) => ({ value: m, label: billingMethodLabels[m] })),
        (r) => r.billingMethod,
      ),
      payment: facet(
        'payment',
        [
          { value: 'on_time', label: 'On time' },
          { value: 'overdue', label: 'Overdue' },
        ],
        standing,
      ),
      terms: facet(
        'terms',
        paymentTerms.map((t) => ({ value: t, label: paymentTermLabels[t] })),
        (r) => r.paymentTerms,
      ),
      locations: facet(
        'locations',
        [
          { value: 'one', label: '1 location' },
          { value: 'several', label: 'Several locations' },
        ],
        count,
      ),
    },
  }
}

export function clientsCsv(rows: readonly ClientRow[]): string {
  // csvLine quotes what needs it and defuses a cell Excel would run as a formula.
  const head = ['Client', 'Locations', 'Point of contact', 'Billing', 'Terms', 'Owed', 'Payment']
  return [
    csvLine(head),
    ...rows.map((r) =>
      csvLine([
        r.name,
        r.locationCount,
        r.contactName,
        r.billingLabel,
        r.terms,
        Math.round(r.owed * 100) / 100,
        r.overdueCount ? `${r.overdueCount} overdue` : 'On time',
      ]),
    ),
  ].join('')
}
